import { z } from "zod";
import { CARD_LANGUAGES, CardLanguage, Condition } from "./types";

// specs/08-settings.md. Saved to the person's account (specs/11 FR-69).
export interface Settings {
  currency: "EUR" | "USD";
  priceSource: "cardmarket" | "tcgplayer";
  priceField: "avg30" | "trend" | "avg7" | "low";
  scanLanguage: CardLanguage;
  conditionMultipliers: Record<Condition, number>;
}

export const DEFAULT_SETTINGS: Settings = {
  currency: "EUR",
  priceSource: "cardmarket",
  priceField: "avg30",
  scanLanguage: "en",
  conditionMultipliers: { NM: 1, LP: 0.85, MP: 0.7, HP: 0.5, DMG: 0.3 },
};

const D = DEFAULT_SETTINGS;
const multiplier = (fallback: number) => z.number().min(0).max(1).catch(fallback);

/**
 * Settings from outside the app (saved settings, specs/11 FR-71; import files, specs/06). Each
 * missing or invalid field falls back to its default and unknown fields are dropped, so
 * settings from an older or newer version of the app still load.
 */
export const settingsSchema = z.object({
  currency: z.enum(["EUR", "USD"]).catch(D.currency),
  priceSource: z.enum(["cardmarket", "tcgplayer"]).catch(D.priceSource),
  priceField: z.enum(["avg30", "trend", "avg7", "low"]).catch(D.priceField),
  scanLanguage: z.enum(CARD_LANGUAGES as [CardLanguage, ...CardLanguage[]]).catch(D.scanLanguage),
  conditionMultipliers: z
    .object({
      NM: multiplier(D.conditionMultipliers.NM),
      LP: multiplier(D.conditionMultipliers.LP),
      MP: multiplier(D.conditionMultipliers.MP),
      HP: multiplier(D.conditionMultipliers.HP),
      DMG: multiplier(D.conditionMultipliers.DMG),
    })
    .catch(D.conditionMultipliers),
});

export function parseSettings(value: unknown): Settings {
  const input = typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};
  return settingsSchema.parse(input);
}
